import { useResource } from '@landsbankinn/l16n';
import type { Prismic } from '@landsbankinn/prismic';
import {
  fallbackImage,
  fallbackRichText,
  fallbackText,
  getDataFromLink,
  PrismicLink,
} from '@landsbankinn/prismic';
import { ArrowButton, CardButton, Segment } from '@landsbankinn/ui';
import React from 'react';
import { CardGroup } from '../components/card-group/CardGroup';
import { OtherCardGroup } from '../components/card-group/OtherCardGroup';
import { HorizontalOtherCard } from '../components/card/HorizontalOtherCard';
import { OtherCard } from '../components/card/OtherCard';
import type { ImgAr } from '../components/img/Img';
import messages from './slices.res';

interface IProps {
  featured: boolean;
  fields: IField[];
}

interface IField {
  image: Prismic.Image;
  title: Prismic.RichText;
  copy: Prismic.RichText;
  link: Prismic.Link;
  link_text: Prismic.RichText;
  ar: ImgAr;
  isSplittedCard?: boolean;
  isSingleFeatured?: boolean;
}

const OtherCardItem = ({
  image,
  title,
  copy,
  link,
  link_text,
  ar,
  isSplittedCard,
  isSingleFeatured,
  ...rest
}: IField) => {
  const { r } = useResource();
  const linkdata = getDataFromLink(link);
  const linkText =
    fallbackText(link_text, linkdata.title ?? undefined) ||
    r(messages.readMore);

  if (isSingleFeatured) {
    return (
      <HorizontalOtherCard
        title={fallbackText(title, linkdata.title ?? undefined)}
        description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
        image={fallbackImage(image, linkdata.image ?? undefined)}
        ar={ar}
        isSplittedCard={true}
        action={
          <PrismicLink value={link} title={linkText}>
            <ArrowButton title={linkText} isSmall />
          </PrismicLink>
        }
        {...rest}
      />
    );
  }

  return (
    <OtherCard
      link={link}
      title={fallbackText(title, linkdata.title ?? undefined)}
      description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
      image={fallbackImage(image, linkdata.image ?? undefined)}
      ar={ar}
      isSplittedCard={isSplittedCard}
      action={
        <PrismicLink value={link} title={linkText}>
          <CardButton title={linkText} />
        </PrismicLink>
      }
      {...rest}
    />
  );
};

export const OtherCardsContainer = ({ fields, featured }: IProps) => {
  const items = fields?.filter(({ link }) => link);
  if (items.length === 0) {
    return null;
  }
  const count = fields.length;
  const isSingleFeatured = fields.length === 1;
  const columns = count <= 2 ? 2 : 3;

  if ((featured === true && count === 3) || isSingleFeatured) {
    return (
      <Segment>
        <OtherCardGroup featured={featured} isSingleFeatured={isSingleFeatured}>
          {items.map(({ ar, ...rest }, index) => {
            return (
              <OtherCardItem
                key={`other-card-item-${index}`}
                ar={
                  featured && index === 0
                    ? 'ar_1_1'
                    : featured && index === 1
                    ? 'ar_16_9'
                    : 'ar_4_3'
                }
                isSplittedCard={!!featured && index === 0}
                isSingleFeatured={isSingleFeatured}
                {...rest}
              />
            );
          })}
        </OtherCardGroup>
      </Segment>
    );
  }

  return (
    <Segment>
      <CardGroup columns={columns}>
        {items.map(({ ar, ...rest }, index) => {
          return (
            <OtherCardItem
              key={`other-card-item-${index}`}
              ar={
                featured && index === 0
                  ? 'ar_1_1'
                  : featured && index === 1
                  ? 'ar_16_9'
                  : 'ar_4_3'
              }
              isSplittedCard={false}
              isSingleFeatured={false}
              {...rest}
            />
          );
        })}
      </CardGroup>
    </Segment>
  );
};
